import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { iaApi } from '../../../lib/api';
import { Download, FileText, Trash2, Upload, KeyRound } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';

export const PosesionDialog = ({
  isOpen,
  setIsOpen,
  selectedUnidad,
  onReload,
  toast,
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const posesionActual = selectedUnidad?.posesion_archivo;

  const handleClose = (open) => {
    setIsOpen(open);
    if (!open) setFile(null);
  };

  const handleUpload = async () => {
    if (!file || !selectedUnidad) return;
    setUploading(true);
    try {
      await iaApi.uploadPosesion(selectedUnidad.unidad_id, file);
      toast.success(posesionActual ? 'Posesión reemplazada' : 'Posesión cargada');
      setFile(null);
      onReload?.();
      setIsOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Error al subir el documento de posesión');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedUnidad) return;
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(iaApi.downloadPosesionUrl(selectedUnidad.unidad_id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al descargar');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = posesionActual?.filename || 'posesion';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Error al descargar el archivo');
    }
  };

  const handleDelete = async () => {
    if (!selectedUnidad) return;
    if (!confirm('¿Eliminar el documento de posesión de esta unidad? La escritura y la carta oferta no se verán afectadas.')) return;
    setDeleting(true);
    try {
      await iaApi.deletePosesion(selectedUnidad.unidad_id);
      toast.success('Documento de posesión eliminado');
      onReload?.();
      setIsOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="posesion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-indigo-600" />
            Posesión - {selectedUnidad?.nombre_unidad}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Acta o documento que acredita la entrega de la posesión efectiva de la unidad.
            Es independiente de la escritura y de la carta oferta.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {posesionActual ? (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-5 h-5 text-indigo-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-indigo-900 truncate" data-testid="posesion-filename">
                      {posesionActual.filename}
                    </p>
                    <p className="text-xs text-indigo-700">
                      {posesionActual.size ? `${(posesionActual.size / 1024).toFixed(1)} KB` : ''}
                      {posesionActual.uploaded_at && (
                        <> · Cargado: {new Date(posesionActual.uploaded_at).toLocaleDateString('es-AR')}</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                    data-testid="posesion-download-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Descargar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={handleDelete}
                    disabled={deleting}
                    data-testid="posesion-delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500 text-center py-2">
              No hay documento de posesión cargado para esta unidad.
            </div>
          )}

          <div className="space-y-2">
            <Label>{posesionActual ? 'Reemplazar posesión' : 'Cargar posesión'}</Label>
            <Input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="posesion-file-input"
            />
            {file && (
              <p className="text-xs text-slate-600">
                Archivo seleccionado: <span className="font-medium">{file.name}</span>
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cerrar
          </Button>
          <Button
            disabled={!file || uploading}
            onClick={handleUpload}
            data-testid="posesion-upload-btn"
          >
            <Upload className="w-4 h-4 mr-1" />
            {uploading ? 'Subiendo...' : (posesionActual ? 'Reemplazar' : 'Cargar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PosesionDialog;
